import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useReducer } from 'react';
import { BUS_TOPIC_SGCONNECT_USER_INFO, getWidgetBus, UserInfo } from '@sgwt/react-shared-libs';
import axios from 'axios';
import { noop } from '../helpers';
import { UserService } from '../services';
import { IUser, ProfileType, ReducerActionMap } from '../types';

export type UserFull = UserInfo<string> & IUser;

type SessionContextState = {
  profileType: ProfileType | null;
  loading: boolean;
  userFull: UserFull | undefined;
};

const initialState: SessionContextState = {
  profileType: null,
  loading: true,
  userFull: undefined,
};

type SessionContextType = SessionContextState & {
  setProfileType: (profileType: ProfileType | null) => void;
  setUserFull: (userFull: UserFull | undefined) => void;
  getHomePathname: () => string;
};

const SessionContext = createContext<SessionContextType>({
  ...initialState,
  setProfileType: noop,
  setUserFull: noop,
  getHomePathname: () => '/explore/all',
});

SessionContext.displayName = 'SessionContext';

const fetchUser = async (): Promise<IUser> => {
  const request = axios.CancelToken.source();
  return UserService.getUser(request);
};

enum Types {
  SET_PROFILE_TYPE = 'SET_PROFILE_TYPE',
  SET_FULL_USER = 'SET_FULL_USER',
  SET_LOADING_USER = 'SET_LOADING_USER',
}

type SessionPayload = {
  [Types.SET_PROFILE_TYPE]: {
    profileType: ProfileType | null;
  };
  [Types.SET_FULL_USER]: {
    userFull: UserFull | undefined;
  };
  [Types.SET_LOADING_USER]: {
    loading: boolean;
  };
};

type SessionActions = ReducerActionMap<SessionPayload>[keyof ReducerActionMap<SessionPayload>];

export const SessionReducer = (state: SessionContextState, action: SessionActions): SessionContextState => {
  switch (action.type) {
    case Types.SET_PROFILE_TYPE: {
      const { profileType } = action.payload;
      return {
        ...state,
        profileType: profileType,
      };
    }

    case Types.SET_FULL_USER: {
      const { userFull } = action.payload;
      return {
        ...state,
        userFull,
      };
    }

    case Types.SET_LOADING_USER: {
      const { loading } = action.payload;
      return {
        ...state,
        loading,
      };
    }

    default: {
      return state;
    }
  }
};

const SessionContextProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(SessionReducer, initialState);

  const pathName = state.profileType === 'Application manager' ? '/explore/mine' : '/explore/all';

  const setProfileType = (profileType: ProfileType | null) =>
    dispatch({ type: Types.SET_PROFILE_TYPE, payload: { profileType } });

  const setUserFull = (userFull: UserFull | undefined) =>
    dispatch({ type: Types.SET_FULL_USER, payload: { userFull } });

  useEffect(() => {
    const setLoadingUser = (loading: boolean) => dispatch({ type: Types.SET_LOADING_USER, payload: { loading } });

    const bus = getWidgetBus();
    const userSubscription = bus.subscribe<UserInfo<string> | undefined>(BUS_TOPIC_SGCONNECT_USER_INFO, (userInfo) => {
      if (userInfo) {
        fetchUser().then((user: IUser) => {
          setUserFull({ ...userInfo, ...user });
          setProfileType(user.profileType);
          setLoadingUser(false);
        });
      }
    });
    return () => {
      bus.unsubscribe(userSubscription);
    };
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      setProfileType,
      setUserFull,
      getHomePathname: () => pathName,
    }),
    [state, pathName]
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (typeof context === 'undefined') {
    throw new Error(`useSession must be used within an SessionContextProvider`);
  }
  return context;
};

export { SessionContext, SessionContextProvider };
